import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const empty = { name: "", address: "", gstin: "", pan: "", state: "West Bengal", state_code: "19", phone: "", email: "",
  default_billing_mode: "split", default_combined_rate: 0, default_gst_percent: 5 };

export default function Parties() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setItems((await api.get("/parties")).data);
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const startEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editId) await api.put(`/parties/${editId}`, form);
      else await api.post("/parties", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this party?")) return;
    await api.delete(`/parties/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-5xl mx-auto" data-testid="parties-page">
      <PageHeader
        title="Parties"
        subtitle="Clients / consignees you bill — managed centrally for fast bill creation."
        action={
          <Button data-testid="add-party-btn" onClick={startNew} className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Party
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-3 py-3">GSTIN</th>
              <th className="text-left px-3 py-3">PAN</th>
              <th className="text-left px-3 py-3">State</th>
              <th className="text-left px-3 py-3">Billing Mode</th>
              <th className="text-left px-3 py-3">Phone</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-brand-muted text-sm">No parties yet</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`party-row-${p.id}`}>
                <td className="px-5 py-3 font-medium">
                  <Link to={`/ledger/party/${p.id}`} className="hover:text-brand-forest underline-offset-4 hover:underline">{p.name}</Link>
                </td>
                <td className="px-3 py-3 font-mono text-xs">{p.gstin || "—"}</td>
                <td className="px-3 py-3 font-mono text-xs">{p.pan || "—"}</td>
                <td className="px-3 py-3">{p.state} · {p.state_code}</td>
                <td className="px-3 py-3 text-xs">
                  {(p.default_billing_mode || "split") === "combined" ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded-sm bg-brand-sand/30 text-brand-ink font-medium">COMBINED</span>
                      <span className="text-brand-muted">₹{p.default_combined_rate || 0}/unit · {p.default_gst_percent || 0}%</span>
                    </span>
                  ) : <span className="text-brand-muted">Split</span>}
                </td>
                <td className="px-3 py-3">{p.phone || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(p)} data-testid={`edit-party-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta" onClick={() => remove(p.id)} data-testid={`delete-party-${p.id}`} data-admin-only="true"><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit Party" : "Add Party"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name *"><Input data-testid="party-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="GSTIN"><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="uppercase font-mono" /></Field>
            <Field label="PAN"><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="uppercase font-mono" /></Field>
            <Field label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
            <Field label="State Code"><Input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} /></Field>
            <div className="md:col-span-2"><Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
            <div className="md:col-span-2"><Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field></div>

            <div className="md:col-span-2 mt-2 pt-3 border-t border-brand-line">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted mb-2">Default Billing Mode</div>
              <div className="text-xs text-brand-muted mb-3">
                Choose how this party is billed by default. <b>Split</b> = separate Material & Transport bills. <b>Combined</b> = one inclusive ₹/unit rate covering material + freight; transporter & supplier still tracked separately.
              </div>
            </div>
            <Field label="Billing Mode">
              <Select value={form.default_billing_mode} onValueChange={(v) => setForm({ ...form, default_billing_mode: v })}>
                <SelectTrigger data-testid="party-billing-mode" className="h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="split">Split (Material + Transport separate)</SelectItem>
                  <SelectItem value="combined">Combined (one inclusive rate)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Default GST %">
              <Select value={String(form.default_gst_percent)} onValueChange={(v) => setForm({ ...form, default_gst_percent: Number(v) })}>
                <SelectTrigger data-testid="party-default-gst" className="h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.default_billing_mode === "combined" && (
              <div className="md:col-span-2">
                <Field label="Default Combined Rate (₹ per unit)">
                  <Input data-testid="party-default-combined-rate" type="number" step="0.01"
                    value={form.default_combined_rate}
                    onChange={(e) => setForm({ ...form, default_combined_rate: Number(e.target.value) })} />
                </Field>
                <p className="text-[11px] text-brand-muted mt-1.5">Auto-fills truck entry; can be overridden per truck.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-party-btn" className="rounded-sm bg-brand-forest text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">{label}</Label>
      <div className="mt-1.5 [&_input]:h-10 [&_input]:rounded-sm [&_input]:bg-white">{children}</div>
    </div>
  );
}
